import { useState, useEffect, useId } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Separator } from '@/components/ui/separator';
import { Braces, AlertTriangle } from 'lucide-react';
import {
  type DetectedLiteral,
  type ParamConfig,
  buildParameterAnnotation,
} from '@/utils/scadExtractUtils';

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface ExtractToVariableResult {
  varName: string;
  replaceAll: boolean;
  makeParameter: boolean;
  tooltip: string;
  /** The full inline annotation comment string (e.g. "[0:200]"), or empty. */
  annotation: string;
}

export interface ExtractToVariableDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (result: ExtractToVariableResult) => void;
  /** The raw literal text that was selected. */
  literal: string;
  /** Detected type of the literal. */
  detectedLiteral: DetectedLiteral;
  /** Pre-filled variable name suggestion. */
  suggestedName: string;
  /** Count of identical occurrences elsewhere in the file (excluding the selection). */
  otherOccurrenceCount: number;
  /** Existing top-level variable names for conflict checking. */
  existingVarNames: string[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const VALID_IDENT_RE = /^[a-zA-Z_$][a-zA-Z0-9_$]*$/;

function isValidIdent(name: string): boolean {
  return VALID_IDENT_RE.test(name);
}

/** Suggest per-axis labels based on the variable name. */
function guessAxisLabels(
  varName: string,
  n: number,
): { xLabel: string; yLabel: string; zLabel?: string } {
  const lower = varName.toLowerCase();
  if (lower.includes('pos') || lower.includes('offset')) {
    return n === 3
      ? { xLabel: 'x', yLabel: 'y', zLabel: 'z' }
      : { xLabel: 'x', yLabel: 'y' };
  }
  if (lower.includes('size') || lower.includes('dim')) {
    return n === 3
      ? { xLabel: 'w', yLabel: 'h', zLabel: 'd' }
      : { xLabel: 'w', yLabel: 'h' };
  }
  return n === 3
    ? { xLabel: 'x', yLabel: 'y', zLabel: 'z' }
    : { xLabel: 'x', yLabel: 'y' };
}

/** Returns true when the variable name hints at an angle parameter. */
function looksLikeAngle(name: string): boolean {
  return /angle|rot|deg|tilt|yaw|pitch|roll/i.test(name);
}

// ---------------------------------------------------------------------------
// Sub-components for parameter config fields
// ---------------------------------------------------------------------------

interface FieldProps {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  className?: string;
}

function NumField({ id, label, value, onChange, placeholder }: FieldProps) {
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={id} className="text-xs text-muted-foreground">
        {label}
      </Label>
      <Input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder ?? '—'}
        className="h-7 w-24 font-mono text-xs"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main dialog
// ---------------------------------------------------------------------------

export function ExtractToVariableDialog({
  open,
  onClose,
  onConfirm,
  literal,
  detectedLiteral,
  suggestedName,
  otherOccurrenceCount,
  existingVarNames,
}: ExtractToVariableDialogProps) {
  const baseId = useId();
  const id = (suffix: string) => `${baseId}-${suffix}`;

  // ── Variable section ──────────────────────────────────────────────────────
  const [varName, setVarName] = useState(suggestedName);
  const [replaceAll, setReplaceAll] = useState(true);

  // ── Parameter section ──────────────────────────────────────────────────────
  const [makeParameter, setMakeParameter] = useState(false);
  const [tooltip, setTooltip] = useState('');

  // Number config
  const [numMin, setNumMin] = useState('');
  const [numMax, setNumMax] = useState('');
  const [numStep, setNumStep] = useState('');
  const [isAngle, setIsAngle] = useState(false);

  // String config
  const [strOptions, setStrOptions] = useState('');

  // Vector config (per-axis)
  const [xMin, setXMin] = useState('');
  const [xMax, setXMax] = useState('');
  const [yMin, setYMin] = useState('');
  const [yMax, setYMax] = useState('');
  const [zMin, setZMin] = useState('');
  const [zMax, setZMax] = useState('');
  const [xLabel, setXLabel] = useState('');
  const [yLabel, setYLabel] = useState('');
  const [zLabel, setZLabel] = useState('');

  // Vector shared range (4+ elements)
  const [sharedMin, setSharedMin] = useState('');
  const [sharedMax, setSharedMax] = useState('');

  // Reset state when dialog opens with new props
  useEffect(() => {
    if (!open) return;
    setVarName(suggestedName);
    setReplaceAll(true);
    setMakeParameter(false);
    setTooltip('');
    setNumMin('');
    setNumMax('');
    setNumStep('');
    setIsAngle(looksLikeAngle(suggestedName));
    setStrOptions(literal.startsWith('"') ? literal.slice(1, -1) : '');
    // Axis labels
    const n = detectedLiteral.elementCount ?? 2;
    const labels = guessAxisLabels(suggestedName, n);
    setXLabel(labels.xLabel);
    setYLabel(labels.yLabel);
    setZLabel(labels.zLabel ?? '');
    setXMin('');
    setXMax('');
    setYMin('');
    setYMax('');
    setZMin('');
    setZMax('');
    setSharedMin('');
    setSharedMax('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, suggestedName]);

  // Update isAngle and axis labels when varName changes
  useEffect(() => {
    if (!open) return;
    setIsAngle(looksLikeAngle(varName));
    const n = detectedLiteral.elementCount ?? 2;
    const labels = guessAxisLabels(varName, n);
    setXLabel(labels.xLabel);
    setYLabel(labels.yLabel);
    setZLabel(labels.zLabel ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [varName]);

  // ── Validation ─────────────────────────────────────────────────────────────
  const nameIsEmpty = varName.trim() === '';
  const nameIsInvalid = !nameIsEmpty && !isValidIdent(varName.trim());
  const nameIsConflict =
    isValidIdent(varName.trim()) && existingVarNames.includes(varName.trim());

  const canConfirm = !nameIsEmpty && !nameIsInvalid;

  // ── Build annotation preview ───────────────────────────────────────────────
  function buildCurrentAnnotation(): string {
    if (!makeParameter) return '';
    const cfg = buildCurrentParamConfig();
    if (!cfg) return '';
    return buildParameterAnnotation(cfg);
  }

  function buildCurrentParamConfig(): ParamConfig | null {
    const { type, elementCount } = detectedLiteral;
    switch (type) {
      case 'boolean':
        return { kind: 'boolean' };
      case 'color':
        return { kind: 'color' };
      case 'number': {
        const min = numMin.trim() !== '' ? Number(numMin) : undefined;
        const max = numMax.trim() !== '' ? Number(numMax) : undefined;
        const step = numStep.trim() !== '' ? Number(numStep) : undefined;
        return { kind: 'number', min, max, step, isAngle };
      }
      case 'string': {
        const raw = strOptions.trim();
        const options = raw
          ? raw
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : [];
        // Ensure the current value is present
        const rawValue = literal.startsWith('"')
          ? literal.slice(1, -1)
          : literal;
        if (options.length > 0 && !options.includes(rawValue)) {
          options.unshift(rawValue);
        }
        return { kind: 'string', options };
      }
      case 'number[]': {
        const n = elementCount ?? 2;
        if (n === 2) {
          return {
            kind: 'vector',
            elementCount: 2,
            xMin: xMin !== '' ? Number(xMin) : undefined,
            xMax: xMax !== '' ? Number(xMax) : undefined,
            yMin: yMin !== '' ? Number(yMin) : undefined,
            yMax: yMax !== '' ? Number(yMax) : undefined,
            xLabel: xLabel.trim() || undefined,
            yLabel: yLabel.trim() || undefined,
          };
        }
        if (n === 3) {
          return {
            kind: 'vector',
            elementCount: 3,
            xMin: xMin !== '' ? Number(xMin) : undefined,
            xMax: xMax !== '' ? Number(xMax) : undefined,
            yMin: yMin !== '' ? Number(yMin) : undefined,
            yMax: yMax !== '' ? Number(yMax) : undefined,
            zMin: zMin !== '' ? Number(zMin) : undefined,
            zMax: zMax !== '' ? Number(zMax) : undefined,
            xLabel: xLabel.trim() || undefined,
            yLabel: yLabel.trim() || undefined,
            zLabel: zLabel.trim() || undefined,
          };
        }
        // 4+ elements
        return {
          kind: 'vector',
          elementCount: n,
          sharedMin: sharedMin !== '' ? Number(sharedMin) : undefined,
          sharedMax: sharedMax !== '' ? Number(sharedMax) : undefined,
        };
      }
      default:
        return null;
    }
  }

  const annotationPreview = buildCurrentAnnotation();
  const declarationPreview = `${varName.trim() || '<name>'} = ${literal};${annotationPreview ? ` // ${annotationPreview}` : ''}`;

  // ── Submit ──────────────────────────────────────────────────────────────────
  function handleConfirm() {
    if (!canConfirm) return;
    onConfirm({
      varName: varName.trim(),
      replaceAll,
      makeParameter,
      tooltip: tooltip.trim(),
      annotation: annotationPreview,
    });
    onClose();
  }

  // ── Render ──────────────────────────────────────────────────────────────────
  const { type, elementCount } = detectedLiteral;
  const showParameterFields = makeParameter;

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Braces className="h-5 w-5" />
            Extract to Variable
          </DialogTitle>
          <DialogDescription>
            Replace the selected literal with a named variable declaration.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {/* ── Value chip ─────────────────────────────────────────────── */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Value:</span>
            <code className="rounded bg-muted px-2 py-0.5 font-mono text-sm text-foreground">
              {literal}
            </code>
          </div>

          {/* ── Variable name ──────────────────────────────────────────── */}
          <div className="space-y-1.5">
            <Label htmlFor={id('varname')}>Variable name</Label>
            <Input
              id={id('varname')}
              value={varName}
              onChange={(e) => setVarName(e.target.value)}
              placeholder="variable_name"
              className="font-mono"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter' && canConfirm) handleConfirm();
              }}
            />
            {nameIsInvalid && (
              <p className="flex items-center gap-1 text-xs text-destructive">
                <AlertTriangle className="h-3.5 w-3.5" />
                Must be a valid OpenSCAD identifier (letters, digits, _ , $; not
                starting with a digit).
              </p>
            )}
            {nameIsConflict && (
              <p className="flex items-center gap-1 text-xs text-yellow-500">
                <AlertTriangle className="h-3.5 w-3.5" />A variable named{' '}
                <code className="font-mono">{varName.trim()}</code> already
                exists.
              </p>
            )}
          </div>

          {/* ── Replace all occurrences ────────────────────────────────── */}
          {otherOccurrenceCount > 0 && (
            <div className="flex items-center gap-2">
              <Checkbox
                id={id('replaceall')}
                checked={replaceAll}
                onCheckedChange={(v) => setReplaceAll(!!v)}
              />
              <Label
                htmlFor={id('replaceall')}
                className="cursor-pointer font-normal"
              >
                Replace{' '}
                {otherOccurrenceCount === 1
                  ? '1 other occurrence'
                  : `${otherOccurrenceCount} other occurrences`}
              </Label>
            </div>
          )}

          <Separator />

          {/* ── Parameter section ──────────────────────────────────────── */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Checkbox
                id={id('makeparam')}
                checked={makeParameter}
                onCheckedChange={(v) => setMakeParameter(!!v)}
              />
              <Label htmlFor={id('makeparam')} className="cursor-pointer">
                Expose as parameter
              </Label>
            </div>

            {showParameterFields && (
              <div className="space-y-4 pl-1">
                {/* Tooltip */}
                <div className="space-y-1.5">
                  <Label htmlFor={id('tooltip')} className="text-sm">
                    Description{' '}
                    <span className="text-muted-foreground">(optional)</span>
                  </Label>
                  <Input
                    id={id('tooltip')}
                    value={tooltip}
                    onChange={(e) => setTooltip(e.target.value)}
                    placeholder="Human-readable label shown in the parameter panel"
                    className="text-sm"
                  />
                </div>

                {/* Number config */}
                {type === 'number' && (
                  <div className="space-y-3">
                    <div className="flex flex-wrap gap-3">
                      <NumField
                        id={id('nummin')}
                        label="Min"
                        value={numMin}
                        onChange={setNumMin}
                        placeholder="0"
                      />
                      <NumField
                        id={id('nummax')}
                        label="Max"
                        value={numMax}
                        onChange={setNumMax}
                        placeholder="100"
                      />
                      <NumField
                        id={id('numstep')}
                        label="Step (optional)"
                        value={numStep}
                        onChange={setNumStep}
                        placeholder="—"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={id('isangle')}
                        checked={isAngle}
                        onCheckedChange={(v) => setIsAngle(!!v)}
                      />
                      <Label
                        htmlFor={id('isangle')}
                        className="cursor-pointer text-sm font-normal"
                      >
                        Angle parameter (uses ° notation)
                      </Label>
                    </div>
                  </div>
                )}

                {/* String config */}
                {type === 'string' && (
                  <div className="space-y-1.5">
                    <Label htmlFor={id('stropts')} className="text-sm">
                      Options{' '}
                      <span className="text-muted-foreground">
                        (comma-separated, optional)
                      </span>
                    </Label>
                    <Input
                      id={id('stropts')}
                      value={strOptions}
                      onChange={(e) => setStrOptions(e.target.value)}
                      placeholder={
                        literal.startsWith('"')
                          ? `${literal.slice(1, -1)}, option2, option3`
                          : 'option1, option2'
                      }
                      className="font-mono text-sm"
                    />
                    <p className="text-xs text-muted-foreground">
                      Leave empty for a plain text input. If the current value
                      is not in the list it will be added automatically.
                    </p>
                  </div>
                )}

                {/* Boolean / color — no extra fields */}
                {(type === 'boolean' || type === 'color') && (
                  <p className="text-xs text-muted-foreground">
                    {type === 'boolean'
                      ? 'Boolean parameters are auto-detected — no annotation needed.'
                      : 'Colour parameters are auto-detected — no annotation needed.'}
                  </p>
                )}

                {/* Vector config — 2 elements (XY pad) */}
                {type === 'number[]' && (elementCount ?? 2) === 2 && (
                  <div className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                      XY pad — set per-axis range and optional labels.
                    </p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-muted-foreground">
                          X axis
                        </p>
                        <div className="flex items-center gap-1.5">
                          <Label
                            htmlFor={id('xlabel')}
                            className="w-10 shrink-0 text-xs text-muted-foreground"
                          >
                            Label
                          </Label>
                          <Input
                            id={id('xlabel')}
                            value={xLabel}
                            onChange={(e) => setXLabel(e.target.value)}
                            className="h-7 font-mono text-xs"
                            placeholder="x"
                          />
                        </div>
                        <div className="flex gap-2">
                          <NumField
                            id={id('xmin')}
                            label="Min"
                            value={xMin}
                            onChange={setXMin}
                            placeholder="0"
                          />
                          <NumField
                            id={id('xmax')}
                            label="Max"
                            value={xMax}
                            onChange={setXMax}
                            placeholder="100"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <p className="text-xs font-medium text-muted-foreground">
                          Y axis
                        </p>
                        <div className="flex items-center gap-1.5">
                          <Label
                            htmlFor={id('ylabel')}
                            className="w-10 shrink-0 text-xs text-muted-foreground"
                          >
                            Label
                          </Label>
                          <Input
                            id={id('ylabel')}
                            value={yLabel}
                            onChange={(e) => setYLabel(e.target.value)}
                            className="h-7 font-mono text-xs"
                            placeholder="y"
                          />
                        </div>
                        <div className="flex gap-2">
                          <NumField
                            id={id('ymin')}
                            label="Min"
                            value={yMin}
                            onChange={setYMin}
                            placeholder="0"
                          />
                          <NumField
                            id={id('ymax')}
                            label="Max"
                            value={yMax}
                            onChange={setYMax}
                            placeholder="100"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Vector config — 3 elements (XYZ pad) */}
                {type === 'number[]' && (elementCount ?? 0) === 3 && (
                  <div className="space-y-3">
                    <p className="text-xs text-muted-foreground">
                      XYZ pad — set per-axis range and optional labels.
                    </p>
                    <div className="grid grid-cols-3 gap-x-4 gap-y-3">
                      {(
                        [
                          {
                            axis: 'X',
                            labelVal: xLabel,
                            setLabelVal: setXLabel,
                            minVal: xMin,
                            setMinVal: setXMin,
                            maxVal: xMax,
                            setMaxVal: setXMax,
                            idSuffix: 'x',
                          },
                          {
                            axis: 'Y',
                            labelVal: yLabel,
                            setLabelVal: setYLabel,
                            minVal: yMin,
                            setMinVal: setYMin,
                            maxVal: yMax,
                            setMaxVal: setYMax,
                            idSuffix: 'y',
                          },
                          {
                            axis: 'Z',
                            labelVal: zLabel,
                            setLabelVal: setZLabel,
                            minVal: zMin,
                            setMinVal: setZMin,
                            maxVal: zMax,
                            setMaxVal: setZMax,
                            idSuffix: 'z',
                          },
                        ] as const
                      ).map(
                        ({
                          axis,
                          labelVal,
                          setLabelVal,
                          minVal,
                          setMinVal,
                          maxVal,
                          setMaxVal,
                          idSuffix,
                        }) => (
                          <div key={axis} className="space-y-2">
                            <p className="text-xs font-medium text-muted-foreground">
                              {axis} axis
                            </p>
                            <div className="flex items-center gap-1">
                              <Label
                                htmlFor={id(`${idSuffix}label`)}
                                className="w-8 shrink-0 text-xs text-muted-foreground"
                              >
                                Label
                              </Label>
                              <Input
                                id={id(`${idSuffix}label`)}
                                value={labelVal}
                                onChange={(e) => setLabelVal(e.target.value)}
                                className="h-7 font-mono text-xs"
                                placeholder={idSuffix}
                              />
                            </div>
                            <NumField
                              id={id(`${idSuffix}min`)}
                              label="Min"
                              value={minVal}
                              onChange={setMinVal}
                              placeholder="0"
                            />
                            <NumField
                              id={id(`${idSuffix}max`)}
                              label="Max"
                              value={maxVal}
                              onChange={setMaxVal}
                              placeholder="100"
                            />
                          </div>
                        ),
                      )}
                    </div>
                  </div>
                )}

                {/* Vector config — 4+ elements (shared range) */}
                {type === 'number[]' && (elementCount ?? 0) >= 4 && (
                  <div className="space-y-2">
                    <p className="text-xs text-muted-foreground">
                      Shared range applied to all elements.
                    </p>
                    <div className="flex gap-3">
                      <NumField
                        id={id('sharedmin')}
                        label="Min"
                        value={sharedMin}
                        onChange={setSharedMin}
                        placeholder="0"
                      />
                      <NumField
                        id={id('sharedmax')}
                        label="Max"
                        value={sharedMax}
                        onChange={setSharedMax}
                        placeholder="100"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Live preview ───────────────────────────────────────────── */}
          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Preview:</p>
            <pre className="overflow-x-auto rounded-md border border-border bg-muted/50 p-3 font-mono text-xs leading-relaxed text-foreground">
              {showParameterFields && tooltip.trim() && (
                <span className="text-green-500 dark:text-green-400">
                  {`// ${tooltip.trim()}\n`}
                </span>
              )}
              <span>{declarationPreview}</span>
            </pre>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleConfirm} disabled={!canConfirm}>
            Extract
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
